import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { SvgIcon } from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { checkProfileCompletion, API_BASE_URL } from '../../services/api';
import Dialog from '../dialogs/Dialog';

interface AppleLoginButtonProps {
  onLoginSuccess: (user: any) => void;
  redirectPath: string;
}

const AppleIcon = (props: any) => (
  <SvgIcon {...props}>
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </SvgIcon>
);

// Sign in with Apple JSのグローバル型定義
declare global {
  interface Window {
    AppleID: {
      auth: {
        init: (config: any) => void;
        signIn: () => Promise<any>;
      };
    };
  }
}

const AppleLoginButton: React.FC<AppleLoginButtonProps> = ({ onLoginSuccess, redirectPath }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isAppleLoaded, setIsAppleLoaded] = useState(false);

  useEffect(() => {
    // Apple IDスクリプトが読み込まれているか確認
    const checkAppleScript = setInterval(() => {
      if (window.AppleID) {
        clearInterval(checkAppleScript);

        // Apple ID認証の初期化
        window.AppleID.auth.init({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: `${process.env.REACT_APP_FRONTEND_URL}/auth/apple/callback`,
          usePopup: true
        });

        setIsAppleLoaded(true);
      }
    }, 100);

    // 10秒後にタイムアウト
    setTimeout(() => {
      clearInterval(checkAppleScript);
      if (!window.AppleID) {
        console.error('Apple ID script failed to load');
      }
    }, 10000);

    return () => clearInterval(checkAppleScript);
  }, []);

  const handleAppleLogin = async () => {
    if (!isAppleLoaded) {
      setError('Apple認証の準備ができていません。ページを再読み込みしてください。');
      return;
    }

    try {
      const response = await window.AppleID.auth.signIn();

      // バックエンドにAppleの認証情報を送信
      const backendResponse = await axios.post(`${API_BASE_URL}/auth/apple/login/token/`, {
        authorization: response.authorization,
        user: response.user
      });

      // トークンを保存
      localStorage.setItem('token', backendResponse.data.access_token);
      localStorage.setItem('refreshToken', backendResponse.data.refresh_token);

      // ユーザー情報を保存
      localStorage.setItem('user', JSON.stringify(backendResponse.data.user));

      // プロフィール完成度チェック
      const isProfileComplete = await checkProfileCompletion();

      // 完成度に基づいて遷移
      if (!isProfileComplete) {
        navigate('/profile/settings', { replace: true });
      } else {
        navigate(redirectPath, { replace: true });
      }

      // ヘッダーの状態を更新
      onLoginSuccess(backendResponse.data.user);
    } catch (error: any) {
      console.error('Apple login failed', error);
      setError(error.response?.data?.error || 'ログインに失敗しました');
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleAppleLogin}
        startIcon={<AppleIcon />}
        fullWidth
        disabled={!isAppleLoaded}
        sx={{
          backgroundColor: '#000000',
          color: '#ffffff',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#1a1a1a',
            boxShadow: '0 1px 2px 0 rgba(0,0,0,0.3), 0 1px 3px 1px rgba(0,0,0,0.15)'
          },
          '&:disabled': {
            backgroundColor: '#cccccc',
            color: '#666666'
          },
          mt: 2,
          textTransform: 'none',
          fontWeight: 500
        }}
      >
        Appleでログイン
      </Button>

      <Dialog
        open={!!error}
        onClose={handleCloseError}
        title="エラー"
        content={error || ''}
      />
    </>
  );
};

export default AppleLoginButton;
